import {format} from 'date-fns';
import {useLogger, useState, watch, type Host, type Language} from '#imports';
import {useBrowserDetection} from '@malt/browser-detection';
import {useLocale} from './useLocale';
import {getIntlPatternFormatDateToShort, getIntlPatternFormatDateToShortWithHours, getIntlPatternFormatTimeToShort} from '../utils/date.utils';

export const useFormat = () => {
    const {locale} = useLocale();
    const logger = useLogger();
    const {name, isCurrentVersionLessThan} = useBrowserDetection();

    const patternDateToShort = useState('use-format:pattern-date-to-short', () => getIntlPatternFormatDateToShort(locale.value));
    const patternDateToShortWithHours = useState('use-format:pattern-date-to-short-with-hours', () =>
        getIntlPatternFormatDateToShortWithHours(locale.value),
    );
    const patternTimeToShort = useState('use-format:pattern-time-to-short', () => getIntlPatternFormatTimeToShort(locale.value));

    // Only watch for locale changes on client-side
    // Server-side watchers can cause memory leaks in SSR as they may not be properly disposed
    if (import.meta.client) {
        watch(locale, () => {
            patternDateToShort.value = getIntlPatternFormatDateToShort(locale.value);
            patternDateToShortWithHours.value = getIntlPatternFormatDateToShortWithHours(locale.value);
            patternTimeToShort.value = getIntlPatternFormatTimeToShort(locale.value);
        });
    }

    /**
     * Format a date using the current locale (detected with i18next)
     *
     * @param {(string|Date|null|undefined)} dateInput - A Date object or a string
     * @param {Intl.DateTimeFormatOptions} options - Options for formatting
     * @returns {string} Return formatted date.
     *
     * Example:
     *
     * - 1 juin 2022 (in french)
     * - 1 June 2022 (in english)
     */
    function formatDate(dateInput: Date | string | null | undefined, options: Intl.DateTimeFormatOptions = {dateStyle: 'long'}): string {
        try {
            if (!dateInput) {
                return '';
            }
            const dateToFormat = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;

            // FIXME: Chrome/Node versions handle date format differently for non standard locale like en-FR and we choose to fallback to en for these cases.
            // https://developer.chrome.com/release-notes/143
            const standardizedLocale = locale.value === 'en-FR' ? 'en' : locale.value;

            return new Intl.DateTimeFormat(standardizedLocale, options).format(dateToFormat);
        } catch (e) {
            logger.error('Invalid time value');
            return '';
        }
    }
    function _formatDateWithPattern(dateInput: string | Date | undefined | null, pattern: string): string {
        if (!dateInput) {
            return '';
        }
        const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
        return format(date, pattern);
    }

    function formatDateToLongWithTimeToShort(dateInput: string | Date | undefined | null): string {
        return formatDate(dateInput, {dateStyle: 'long', timeStyle: 'short'});
    }

    /**
     * Format a date string using the current locale (detected with i18next)
     *
     * @param {(string|Date|null|undefined)} dateInput - A Date object or a string
     * @returns {string} Return formatted date in short date style
     *
     * Examples: 31/01/2025 for fr-FR, 1/31/2025 for en-US
     */
    function formatDateToShort(dateInput: string | Date | undefined | null): string {
        return _formatDateWithPattern(dateInput, patternDateToShort.value);
    }

    /**
     * Format a date string using the current locale (detected with i18next)
     *
     * @param {(string|Date|null|undefined)} dateInput - A Date object or a string
     * @param {Intl.DateTimeFormatOptions} options - Options for formatting
     * @returns {string} Return formatted date in short date style with hours
     *
     * Example:
     *
     * - 31/01/2025 08:23:24 (in fr-FR)
     * - 1/31/2022 08:23:24 (in en-US)
     */
    function formatDateToShortWithHours(dateInput: string | Date | undefined | null): string {
        return _formatDateWithPattern(dateInput, patternDateToShortWithHours.value);
    }

    function formatTimeToShort(dateInput: string | Date | undefined | null): string {
        return _formatDateWithPattern(dateInput, patternTimeToShort.value);
    }

    function getCurrencyOptions(currencyCode?: string, currencyOptions = {decimals: 0}): Intl.NumberFormatOptions {
        if (!currencyCode) {
            logger.warn('currencyCode should not be undefined');
        }

        /**
         * Safari less than 14.1.0 does not support currencyDisplay to 'narrowSymbol' so we fallback to 'symbol'
         */
        if (name.value === 'safari' && isCurrentVersionLessThan('14.1.0')) {
            return currencyCode
                ? {
                      style: 'currency',
                      currency: currencyCode,
                      currencyDisplay: 'symbol',
                      minimumFractionDigits: currencyOptions.decimals,
                      maximumFractionDigits: currencyOptions.decimals,
                  }
                : {};
        }

        return currencyCode
            ? {
                  style: 'currency',
                  currency: currencyCode,
                  currencyDisplay: 'narrowSymbol',
                  minimumFractionDigits: currencyOptions.decimals,
                  maximumFractionDigits: currencyOptions.decimals,
              }
            : {};
    }

    /**
     * Format an amount using the current locale (detected with i18next)
     *
     * @param {string} currencyCode
     * @param {number} amount
     * @param {*} options - Options for formatting
     * @returns {string} Return formatted amount
     *
     * Example:
     *
     * - 1000 € (in fr-FR)
     * - 1,000$ (in en-US)
     *
     */
    function formatAmount(currencyCode: string | undefined, amount: number, currencyOptions = {decimals: 0}): string {
        const options = getCurrencyOptions(currencyCode, currencyOptions);
        return new Intl.NumberFormat(locale.value, options).format(amount);
    }

    /**
     * Format an number using the current locale (detected with i18next)
     *
     * @param {number | string | undefined} number
     * @returns {string} Return formatted number
     *
     * Example:
     *
     * - 1 000 (in fr-FR)
     * - 1,000 (in en-US)
     *
     */
    function formatNumber(number: number | string | undefined): string {
        if (number == null || isNaN(Number(number))) {
            return '0';
        }
        return new Intl.NumberFormat(locale.value).format(Number(number));
    }

    /**
     * Formats a file size in bytes to a human readable string.
     * @param size - The size in bytes
     * @returns The formatted size
     */
    function formatFileSize(size: number | undefined): string {
        const byteFormat = {
            notation: 'compact',
            style: 'unit',
            unit: 'byte',
            unitDisplay: 'narrow',
        } as const;
        if (size == null) {
            return Intl.NumberFormat(locale.value, byteFormat).format(0);
        }
        return new Intl.NumberFormat(locale.value, byteFormat).format(size);
    }

    /**
     * Get the currency symbol from a currency code and using current locale (detected with i18next)
     *
     * @param {string} currencyCode
     * @returns {string}
     *
     */
    function getCurrencySymbolFrom(currencyCode: string | undefined): string {
        const options = getCurrencyOptions(currencyCode);
        const symbol = new Intl.NumberFormat(locale.value, options).formatToParts(1).find((x) => x.type === 'currency');
        if (symbol === undefined) {
            return '';
        }
        return symbol?.value;
    }

    function formatPercent(percentage: number, options: Intl.NumberFormatOptions = {maximumFractionDigits: 2}): string {
        return (percentage / 100).toLocaleString(locale.value, {
            style: 'percent',
            ...options,
        });
    }

    // String formatters
    function capitalize(string: string) {
        return string.charAt(0).toUpperCase() + string.slice(1);
    }
    function capitalizeAllWords(string: string) {
        // split with space or dash
        return string.split(' ').map(capitalize).join(' ');
    }

    // Localization formatters
    function formatCountryName(host: Host, fallback = ''): string {
        if (!locale.value || !host.countryCode) {
            return fallback;
        }
        const regionNamesInEnglish = new Intl.DisplayNames([locale.value], {type: 'region'});
        return capitalizeAllWords(regionNamesInEnglish.of(host.countryCode) ?? host.countryName);
    }

    function formatLanguageName(language: Language, fallback = ''): string {
        if (!locale.value || !language.language) {
            return fallback;
        }
        const languageNamesInEnglish = new Intl.DisplayNames([locale.value], {type: 'language'});
        return capitalizeAllWords(languageNamesInEnglish.of(language.language) ?? language.label);
    }

    return {
        // functions A-Z
        capitalize,
        capitalizeAllWords,
        formatAmount,
        formatCountryName,
        formatDate,
        formatDateToLongWithTimeToShort,
        formatDateToShort,
        formatDateToShortWithHours,
        formatFileSize,
        formatNumber,
        formatTimeToShort,
        formatLanguageName,
        formatPercent,
        getCurrencySymbolFrom,
    };
};
